// ---------------------------------------------------------------------------
// Structured data (schema.org JSON-LD) for the SPA marketing routes.
//
// The static blog already emits its own BlogPosting/Blog graph (see
// blog/render.mjs). This module is the SPA's counterpart: it gives the public
// marketing pages the machine-readable layer that search engines use for rich
// results and that AI answer engines (ChatGPT, Perplexity, Google AI Overviews,
// Claude) lean on to understand and cite the product.
//
// It's injected from seo.ts's applyDocumentHead, which runs before the prerender
// readiness marker fires — so the headless snapshot a crawler captures already
// carries the JSON-LD. Everything here is factual and verifiable: the free tier
// is real, the FAQ text is the exact copy shown on the page, and no ratings or
// review counts are claimed until there are genuine reviews to back them.
// ---------------------------------------------------------------------------

import { SITE_ORIGIN } from './utils/siteOrigin';
import { FAQ_ITEMS } from './content/faq';
import { PUBLIC_PAGES, PUBLIC_PAGES_BY_PATH } from '../../../methods/src/common/publicPages';

// Stable @id anchors so nodes can reference one another across the graph.
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const LOGO_URL = `${SITE_ORIGIN}/icon-512.png`;

// The brand's verified public profiles — the `sameAs` set that ties this
// Organization node to its real presences (mirrors src/components/SocialLinks).
const SAME_AS = [
  'https://www.instagram.com/entre_dash/',
  'https://www.facebook.com/profile.php?id=61590697092951',
  'https://x.com/Entre_Dash',
  'https://www.linkedin.com/company/entredash/',
  'https://www.youtube.com/@EntreDash',
];

// The categories EntreDash means to own — a mix of the brand's own category
// language and the plain-English topics it is genuinely authoritative on. Placed
// on `knowsAbout`, the schema.org property for subjects an organization has real
// expertise in, so search and AI engines associate EntreDash with these concepts
// (category creation) rather than us hoping the prose implies it.
const KNOWS_ABOUT = [
  'AI advisory board for entrepreneurs and inventors',
  'spark to business',
  'personal innovation operating system',
  'entrepreneur dashboard',
  'idea incubator',
  'founder workspace',
  '0-to-1 AI execution partner for founders',
  'closing the 0-to-1 founder gap',
  'AI business coach and startup coach',
  'spark ideation',
  'turning an everyday frustration into a business idea',
  'finding a startup idea in problems you already have',
  'startup and invention idea validation',
  'idea validation for solopreneurs and one-person startups',
  'prior art and patentability search',
  'turning an idea into a patentable invention',
  'provisional patent preparation for independent inventors',
  'market validation for new products',
];

/** The Organization node — globally true, emitted on every route. */
function organizationNode(): Record<string, unknown> {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'EntreDash',
    url: `${SITE_ORIGIN}/`,
    logo: { '@type': 'ImageObject', url: LOGO_URL },
    description:
      'EntreDash is the AI advisory board that takes everyday people from a random idea to a real, buildable, patentable invention: AI strategy sessions, real prior-art research, a design workspace, business planning, and an IP education center.',
    slogan: 'Your spark to a real business.',
    knowsAbout: KNOWS_ABOUT,
    sameAs: SAME_AS,
  };
}

/** The WebSite node — globally true, emitted on every route. */
function websiteNode(): Record<string, unknown> {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: `${SITE_ORIGIN}/`,
    name: 'EntreDash',
    publisher: { '@id': ORG_ID },
  };
}

/** A genuine product rating to publish, or null/undefined to publish none. */
export interface AggregateRatingInput {
  /** Mean stars, 1 decimal (e.g. 4.7). */
  average: number;
  /** Number of genuine ratings behind the average. */
  count: number;
}

/**
 * SoftwareApplication node — home only. States the genuinely free tier as a $0
 * offer (the product really does include one free strategy session a month).
 *
 * aggregateRating is emitted ONLY when a real one is passed in — sourced entirely
 * from genuine in-app memo ratings (see the memo_ratings table + getProductRating),
 * and only once there's a credible sample (the caller gates on RATING_MIN_SAMPLE).
 * We never invent a rating: that would violate Google's structured-data policy and
 * contradict the brand's whole premise. With no real ratings yet, `rating` is
 * omitted and this node carries no aggregateRating — exactly as before.
 */
function softwareApplicationNode(rating?: AggregateRatingInput | null): Record<string, unknown> {
  const node: Record<string, unknown> = {
    '@type': 'SoftwareApplication',
    name: 'EntreDash',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: `${SITE_ORIGIN}/`,
    description:
      'Test whether your idea holds up with a guided AI strategy session and a verdict backed by real prior-art research. Start free.',
    keywords:
      'AI advisory board, AI business coach, AI startup coach, business idea validation, startup idea validation, test my idea, spark to business, spark ideation, turn frustration into a business idea, find a business idea, startup idea from a problem, entrepreneur dashboard, personal innovation OS, idea incubator, founder workspace, 0-to-1 founder gap, AI tools for founders, AI tools for solopreneurs, patentability check, prior art search, AI idea validation tool',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free plan: one guided strategy session and memo each month.',
    },
    publisher: { '@id': ORG_ID },
  };

  if (rating && rating.count > 0 && rating.average > 0) {
    node.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: rating.average,
      ratingCount: rating.count,
      bestRating: 5,
      worstRating: 1,
    };
  }

  return node;
}

/**
 * FAQPage node — home only. Built from the exact FAQ copy rendered on the
 * landing page (src/content/faq.ts), so the structured data matches the visible
 * content, which is what Google requires for FAQ rich results and what AI
 * engines quote.
 */
function faqPageNode(items: { q: string; a: string }[] = FAQ_ITEMS): Record<string, unknown> {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

/**
 * A breadcrumb node for a mapped sub-page: Home › <label>. Helps search engines
 * render a breadcrumb trail and situates the page in the site hierarchy.
 */
function breadcrumbNode(pathname: string, label: string): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: label, item: `${SITE_ORIGIN}${pathname}` },
    ],
  };
}

/**
 * WebApplication node for a free-tool page: tells search and AI answer engines this URL is a free,
 * browser-based tool (not an article), what it answers, and who publishes it. The description is the
 * tool's own quick answer, the same text rendered on the page. Never carries a rating.
 */
function freeToolNode(pathname: string): Record<string, unknown> | null {
  const page = PUBLIC_PAGES_BY_PATH[pathname];
  const tool = page?.freeTool;
  if (!page || !tool) return null;
  const url = `${SITE_ORIGIN}${pathname}`;
  return {
    '@type': 'WebApplication',
    '@id': `${url}#tool`,
    name: tool.footerLabel,
    url,
    description: tool.quickAnswer ?? page.description,
    applicationCategory: tool.category ?? 'BusinessApplication',
    operatingSystem: 'Any (web browser)',
    browserRequirements: 'Requires JavaScript.',
    isAccessibleForFree: true,
    inLanguage: 'en',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@id': ORG_ID },
    ...(page.dated?.reviewedOn ? { dateModified: page.dated.reviewedOn } : {}),
  };
}

// Breadcrumb labels for the mapped sub-pages (everything except home), DERIVED
// from the public-page registry (methods/src/common/publicPages.ts) — the single
// source of truth every public page runs through. A page carries a breadcrumb
// iff it declares a `breadcrumbLabel` there, so this can no longer fall out of
// sync with the routes seo.ts maps and prerender.ts snapshots. (This derivation
// is what closed the /spark, /learn, /marketplace breadcrumb gap: they were
// prerendered + titled but had no breadcrumb node until they were registered.)
const BREADCRUMB_LABELS: Record<string, string> = Object.fromEntries(
  PUBLIC_PAGES.filter((p) => p.breadcrumbLabel).map((p) => [p.path, p.breadcrumbLabel as string]),
);

/**
 * Build the JSON-LD `@graph` for a route. Organization + WebSite are globally
 * true and always present; the home page (the registry entry flagged
 * `jsonLd: 'home'`) additionally carries the app + FAQ nodes; every other
 * registered sub-page carries a breadcrumb built from its registry label.
 * Returns the identity graph alone for routes with nothing else to emit.
 */
export function buildStructuredData(
  pathname: string,
  rating?: AggregateRatingInput | null,
): Record<string, unknown> {
  const graph: Record<string, unknown>[] = [organizationNode(), websiteNode()];

  const page = PUBLIC_PAGES_BY_PATH[pathname];
  if (page?.jsonLd === 'home') {
    graph.push(softwareApplicationNode(rating), faqPageNode());
  } else {
    // A sub-page carries a breadcrumb (from its registry label) and, if it
    // declares its own FAQ (a free-tool page), a FAQPage node built from the
    // exact same copy the page renders — so the two never drift.
    if (BREADCRUMB_LABELS[pathname]) graph.push(breadcrumbNode(pathname, BREADCRUMB_LABELS[pathname]));
    if (page?.faq?.length) graph.push(faqPageNode(page.faq));
    const tool = freeToolNode(pathname);
    if (tool) graph.push(tool);
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}
